/**
 * AI Chat Welcome Popup 组件
 * 每个分析记录首次显示时显示
 * 使用 Ant Design Popover 组件
 */

import { FC, ReactNode, useEffect, useRef } from 'react';
import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAIChatStore } from '@/store/useAIChatStore';
import { useUserStore } from '@/store/useUserStore';
import type { ContextType } from '@/types/aiChat';
import { trackEvent } from '@/utils/analytics';
import { EVENTS } from '@/utils/analyticsEvents';
import { buildCoachBaseProps, buildCoachCoreProps } from '@/utils/aiCoachAnalytics';
import styles from './index.module.scss';

interface AIChatWelcomePopupProps {
  contextInfo: { type: ContextType; id: number };
  children: ReactNode;
}

const AIChatWelcomePopup: FC<AIChatWelcomePopupProps> = ({ contextInfo, children }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const popupDisplayedRef = useRef<string>('');
  // 生成上下文 key
  const contextKey = `${contextInfo.type}-${contextInfo.id}`;
  const isDismissed = useAIChatStore((state) => state.welcomePopupDismissed[contextKey] || false);
  const dismissWelcomePopup = useAIChatStore((state) => state.dismissWelcomePopup);
  const visible = !isDismissed;
  const baseProps = buildCoachBaseProps(contextInfo, user?.subscription_plan);
  const coreProps = buildCoachCoreProps(contextInfo);

  useEffect(() => {
    if (!visible) return;
    if (popupDisplayedRef.current === contextKey) return;
    trackEvent(EVENTS.AI_COACH_POPUP_DISPLAYED, baseProps);
    popupDisplayedRef.current = contextKey;
  }, [baseProps, contextKey, visible]);

  // 关闭 Popup
  const handleClose = (dismissMethod?: 'x_button') => {
    if (!isDismissed) {
      if (dismissMethod) {
        trackEvent(EVENTS.AI_COACH_POPUP_DISMISSED, {
          ...coreProps,
          dismiss_method: dismissMethod,
        });
      }
      dismissWelcomePopup(contextKey);
    }
  };

  // Popover 内容
  const content = (
    <div className={styles.popoverContent}>
      {/* SVG 渐变定义 */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="aiPopupIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff6b9d" />
            <stop offset="100%" stopColor="#ff4757" />
          </linearGradient>
        </defs>
      </svg>

      <div className={styles.iconWrapper}>
        <svg className={styles.icon} aria-hidden="true">
          <use xlinkHref="#icon-a-AIchat" fill="url(#aiPopupIconGradient)"></use>
        </svg>
      </div>

      <div className={styles.textContent}>
        <h4 className={styles.title}>{t('aiChat.popup.title')}</h4>
        <p className={styles.subtitle}>{t('aiChat.popup.subtitle')}</p>
      </div>

      <button className={styles.closeButton} onClick={() => handleClose('x_button')} aria-label={t('aiChat.popup.closeAriaLabel')}>
        <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  return (
    <Popover
      content={content}
      open={visible}
      onOpenChange={(open) => !open && handleClose()}
      placement="topRight"
      arrow={false} // 不显示箭头
      classNames={{
        root: styles.aiChatPopover, // 使用 CSS Module 类名
      }}
      styles={{
        root: {
          // 根容器样式 - 添加边框
          background: 'var(--ai-chat-popup-border)',
          padding: '1px',
          borderRadius: '14px',
        },
        container: {
          // 内容容器样式
          background: 'var(--ai-chat-popup-bg)',
          borderRadius: '12px',
          padding: '12px',
          minWidth: '280px',
          maxWidth: '320px',
          boxShadow: 'var(--ai-chat-popup-shadow)',
        },
      }}
      trigger={[]} // 不使用触发器，通过 open 属性控制显示
      destroyOnHidden
    >
      {children}
    </Popover>
  );
};

export default AIChatWelcomePopup;
